import { Injectable, NgZone } from '@angular/core';
import { LinkFormattingService } from './link-formatting.service';
import { SafeHtmlService } from './safe-html.service';

/**
 * Globally scans rendered DOM and converts plain HTTPS URLs into clickable links.
 * Works with Angular interpolation ({{ }}) without per-field template changes.
 */
@Injectable({ providedIn: 'root' })
export class UserGeneratedTextDisplayService {
  private readonly httpsPattern = /https:\/\/[^\s<>"']+/i;
  private readonly skipAncestorSelector =
    'input, textarea, select, option, button, quill-editor, quill-view, .ql-editor, .ql-container, .ql-tooltip, [contenteditable="true"], [noLinkFormat], script, style, noscript, svg, code, .p-editor-content, .p-inputtext, .p-inputtextarea, .p-dropdown-panel input';
  private readonly candidateTags = new Set([
    'TD', 'TH', 'SPAN', 'P', 'DIV', 'LI', 'LABEL', 'DD', 'DT', 'SMALL',
    'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'ARTICLE', 'SECTION', 'BLOCKQUOTE', 'PRE', 'FONT'
  ]);

  private observer: MutationObserver | null = null;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private root: HTMLElement | null = null;
  private enrichVersion = 0;
  private readonly boundPreviewClick = (event: MouseEvent) => this.handlePreviewClick(event);

  constructor(
    private linkFormattingService: LinkFormattingService,
    private safeHtmlService: SafeHtmlService,
    private ngZone: NgZone
  ) {}

  /**
   * Starts observing the application root and formatting user-generated text in the DOM.
   */
  public attach(root: HTMLElement): void {
    if (this.observer) {
      return;
    }

    this.root = root;
    this.observer = new MutationObserver(() => this.scheduleScan());
    this.observer.observe(root, {
      childList: true,
      subtree: true,
      characterData: true
    });

    root.addEventListener('click', this.boundPreviewClick, true);
    this.scheduleScan();
  }

  public detach(): void {
    this.root?.removeEventListener('click', this.boundPreviewClick, true);
    this.observer?.disconnect();
    this.observer = null;
    this.root = null;
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }

  private scheduleScan(): void {
    if (!this.root || this.debounceTimer) {
      return;
    }

    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      this.ngZone.runOutsideAngular(() => {
        requestAnimationFrame(() => this.scan(this.root!));
      });
    }, 50);
  }

  private scan(root: HTMLElement): void {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT);
    let processed = 0;
    let node = walker.currentNode as HTMLElement;

    while (node) {
      if (this.candidateTags.has(node.tagName) && this.httpsPattern.test(node.textContent ?? '')) {
        if (this.processElement(node)) {
          processed++;
        }

        if (processed >= 80) {
          break;
        }
      }

      node = walker.nextNode() as HTMLElement;
    }
  }

  private processElement(element: HTMLElement): boolean {
    if (this.shouldSkip(element)) {
      return false;
    }

    const sourceText = element.textContent ?? '';
    if (!this.httpsPattern.test(sourceText)) {
      return false;
    }

    if (this.linkFormattingService.hasFormattedLinks(element.innerHTML)) {
      return false;
    }

    const sourceKey = this.buildSourceKey(element);
    if (element.dataset.sihcDisplayKey === sourceKey) {
      return false;
    }

    const estHtml = element.innerHTML.includes('<') && element.innerHTML.includes('>');
    const syncFormatted = estHtml
      ? this.linkFormattingService.formatHtmlLinksSync(element.innerHTML)
      : this.linkFormattingService.formatPlainTextLinksSync(sourceText);

    if (!syncFormatted.includes('<a ')) {
      return false;
    }

    element.dataset.sihcDisplayKey = sourceKey;
    element.classList.add('formatted-text');
    element.innerHTML = this.safeHtmlService.sanitizeFormattedHtml(syncFormatted);

    void this.enrichElement(element, sourceText, estHtml, sourceKey);
    return true;
  }

  private async enrichElement(
    element: HTMLElement,
    sourceText: string,
    estHtml: boolean,
    sourceKey: string
  ): Promise<void> {
    const version = ++this.enrichVersion;
    const formatted = await this.linkFormattingService.formatForDisplay(sourceText, estHtml);

    if (
      version !== this.enrichVersion ||
      !element.isConnected ||
      element.dataset.sihcDisplayKey !== sourceKey ||
      !formatted.includes('<a ')
    ) {
      return;
    }

    element.innerHTML = this.safeHtmlService.sanitizeFormattedHtml(formatted);
  }

  private handlePreviewClick(event: MouseEvent): void {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }

    const preview = target.closest('a.sihc-link-preview') as HTMLAnchorElement | null;
    if (!preview) {
      return;
    }

    const url = preview.getAttribute('href') || preview.dataset.sihcLinkUrl || preview.dataset.url;
    if (!url?.startsWith('https://')) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  private shouldSkip(element: HTMLElement): boolean {
    if (element.hasAttribute('noLinkFormat')) {
      return true;
    }

    if (element.closest(this.skipAncestorSelector)) {
      return true;
    }

    if (element.isContentEditable) {
      return true;
    }

    if (!this.candidateTags.has(element.tagName)) {
      return true;
    }

    if (!this.isDisplayLeaf(element)) {
      return true;
    }

    return false;
  }

  private isDisplayLeaf(element: HTMLElement): boolean {
    return Array.from(element.children).every(child =>
      child.tagName === 'BR' || child.tagName === 'WBR'
    );
  }

  private buildSourceKey(element: HTMLElement): string {
    const source = element.textContent ?? '';
    return `${source.length}:${source.slice(0, 96)}`;
  }
}
